import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookOpen, Download, Calendar, Users } from 'lucide-react';

export default function Colles20222023() {
  const { t } = useTranslation();

  // List of files for PC*1 LLG 2022-2023
  const llgFiles = [
    { name: "LLG1", file: "LLG1.pdf", corrige: "LLG1_cor.pdf" },
    { name: "LLG2", file: "LLG2.pdf", corrige: "LLG2_cor.pdf" },
    { name: "LLG3", file: "LLG_3.pdf", corrige: "LLG3_cor.pdf" },
    { name: "LLG-14", file: "LLG-14.pdf", corrige: "Corrigés-LLG-14.pdf" },
    { name: "LLG S2.1", file: "LLG_Colle_S2.1.pdf", corrige: "LLG_Colle_S2.1_cor.pdf" },
    { name: "Colles 4", file: "Colles 4.pdf", corrige: "Corrigés Colle4-5.pdf" },
    { name: "Colles 5", file: "Colles5LLG.pdf", corrige: "Corrige-LLG-5.pdf" },
    { name: "Colles 17", file: "Colles 17_noir.pdf", corrige: "Corrigé LLG 17et166.pdf" },
    { name: "Colles 18", file: "Colles 18.pdf", corrige: "Corrigé LLG 18.pdf" },
    { name: "Colles 4-5", file: "colles4-5.pdf", corrige: "Corrigés Colle4-5.pdf" },
    { name: "Colle 6b", file: "colle6b.pdf", corrige: "Corrige-Turgot-6.pdf" },
  ];

  // List of files for PCSI Turgot 2022-2023
  const turgotFiles = [
    { name: "Turgot 1", file: "Turgot1_mdp.pdf", corrige: "Turgot1_cor.pdf" },
    { name: "Turgot 2", file: "Turgot2.pdf", corrige: "Turgot2_cor.pdf" },
    { name: "Turgot 3", file: "Turgot3.pdf", corrige: "Turgot3_cor.pdf" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/teaching"
          className="inline-flex items-center text-[#735d78] hover:text-[#614c66] mb-4 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('teaching.colles.backToColles')}
        </Link>
        
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="font-display text-5xl md:text-6xl font-semibold text-stone-800 mb-4">
            Colles 2022-2023
          </h1>
          <p className="text-xl text-stone-600">
            {t('teaching.colles.academicYear')} 2022-2023
          </p>
        </motion.div>
      </div>

      {/* PC*1 LLG Section */}
      <motion.div 
        className="bg-[rgba(255,251,246,0.82)] backdrop-blur-sm rounded-[1.75rem] p-8 mb-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)] border border-stone-200/80"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="flex items-center mb-6">
          <BookOpen className="w-8 h-8 text-[#735d78] mr-3" />
          <h2 className="font-display text-4xl font-semibold text-stone-800">PC*1 LLG (22-23)</h2>
        </div>
        
        <p className="text-stone-600 mb-8">
          {t('teaching.colles.llg2022')}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {llgFiles.map((file, index) => (
            <motion.div
              key={file.name}
              className="bg-[linear-gradient(135deg,rgba(250,247,250,0.95),rgba(240,232,240,0.92))] rounded-lg p-4 border border-[#d7d0d9]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 + index * 0.05 }}
            >
              <h4 className="font-semibold text-stone-800 mb-3">{file.name}</h4>
              <div className="space-y-2">
                <a
                  href={`/files/colles22-23/${file.file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white/60 rounded p-2 hover:bg-white/80 transition-colors duration-200"
                >
                  <span className="text-sm text-stone-700">{t('common.enonce')}</span>
                  <Download className="w-4 h-4 text-[#735d78]" />
                </a>
                <a
                  href={`/files/colles22-23/${file.corrige}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white/60 rounded p-2 hover:bg-white/80 transition-colors duration-200"
                >
                  <span className="text-sm text-stone-700">{t('common.corrige')}</span>
                  <Download className="w-4 h-4 text-[#8a6f88]" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* PCSI Turgot Section */}
      <motion.div 
        className="bg-[rgba(255,251,246,0.82)] backdrop-blur-sm rounded-[1.75rem] p-8 mb-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)] border border-stone-200/80"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <div className="flex items-center mb-6">
          <Users className="w-8 h-8 text-[#8a6f88] mr-3" />
          <h2 className="font-display text-4xl font-semibold text-stone-800">PCSI Turgot (22-23)</h2>
        </div>
        
        <p className="text-stone-600 mb-8">
          {t('teaching.colles.turgot2022')}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {turgotFiles.map((file, index) => (
            <motion.div
              key={file.name}
              className="bg-[linear-gradient(135deg,rgba(250,247,250,0.95),rgba(240,232,240,0.92))] rounded-lg p-4 border border-[#d7d0d9]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 + index * 0.05 }}
            >
              <h4 className="font-semibold text-stone-800 mb-3">{file.name}</h4>
              <div className="space-y-2">
                <a
                  href={`/files/colles22-23/${file.file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white/60 rounded p-2 hover:bg-white/80 transition-colors duration-200"
                >
                  <span className="text-sm text-stone-700">{t('common.enonce')}</span>
                  <Download className="w-4 h-4 text-[#8a6f88]" />
                </a>
                <a
                  href={`/files/colles22-23/${file.corrige}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white/60 rounded p-2 hover:bg-white/80 transition-colors duration-200"
                >
                  <span className="text-sm text-stone-700">{t('common.corrige')}</span>
                  <Download className="w-4 h-4 text-[#735d78]" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Note Section */}
      <motion.div 
        className="bg-[linear-gradient(135deg,rgba(250,248,244,0.95),rgba(243,239,231,0.92))] rounded-2xl p-6 border border-stone-200/80"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.0 }}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Calendar className="w-6 h-6 text-[#8a7767] mt-1" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-stone-800 mb-2">{t('teaching.colles.note')}</h3>
            <p className="text-stone-700">
              {t('teaching.colles.noteDesc2022')}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}; 
